// @flow
import { Map } from "immutable"
import * as R from "ramda"

import { get } from "common/utils/get"
import minIn from "common/utils/minIn"
import toArray from "common/utils/toArray"
import cuckoo from "../cuckoo/cuckoo"
import { cuckooEvents } from "../../consts/enums"
import { BAGS_TYPES } from "../../../mmb/scenes/AccountBooking/components/Booking/services/bags/tools"

type SingleTypeParamsValue = string | number
export type SingleTypeBagParams = Map<string, SingleTypeParamsValue>
export type BagParams = Map<string, SingleTypeBagParams>

const paramIntersection = (
  currParams: SingleTypeBagParams,
  // eslint-disable-next-line flowtype/no-weak-types
  flightParams: Object,
): SingleTypeBagParams =>
  Map({
    length: minIn([currParams.get("length"), get("length", flightParams)]),
    width: minIn([currParams.get("width"), get("width", flightParams)]),
    height: minIn([currParams.get("height"), get("height", flightParams)]),
    weight: minIn([currParams.get("weight"), get("weight", flightParams)]),
    note: currParams.get("note"), // TODO: Check
  })

const initialInfParams: SingleTypeBagParams = Map({
  length: Infinity,
  width: Infinity,
  height: Infinity,
  weight: Infinity,
  note: "",
})

export const BAG_INITIAL_NULL_PARAMS: SingleTypeBagParams = Map({
  length: null,
  width: null,
  height: null,
  weight: null,
  note: "",
})

// Create basic flat bag params map based on api format
// eslint-disable-next-line flowtype/no-weak-types
const formatFlatBagParamsMap = (inputBagParams: Array<number> | Object): Object => {
  if (Array.isArray(inputBagParams)) {
    return Map(
      R.zipObj(
        [
          "hand_length",
          "hand_width",
          "hand_height",
          "hand_weight",

          "hold_weight",
          "hold_length",
          "hold_width",
          "hold_height",
          "hold_note",

          "hand2_length",
          "hand2_width",
          "hand2_height",
          "hand2_weight",
          "hand2_note",

          "hand_note", // Yup, really, it's at the end
        ],
        inputBagParams,
      ),
    )
  }
  if (R.is(Object, inputBagParams)) {
    return Map(inputBagParams)
  }
  cuckoo.error(cuckooEvents.INTERNAL_MAPPER_BAGS_UNSUPPORTED_FORMAT, { inputBagParams })
  return Map()
}

// inputBagParams is res["luggage"] || res["bagParams"]
export default (
  inputBagParams: any, // eslint-disable-line flowtype/no-weak-types
  bagsRestrictions: any, // eslint-disable-line flowtype/no-weak-types
  hasPaidHandBagsAllowed: boolean,
  infantsDifferentHandWeight: boolean,
  infantsHandBagWeight: number,
): BagParams => {
  const bagParamsObj = formatFlatBagParamsMap(inputBagParams)

  let bagParams: Map<string, Map<string, SingleTypeParamsValue>> = Map({
    [BAGS_TYPES.HAND]: initialInfParams,
    [BAGS_TYPES.HOLD]: initialInfParams,
    [BAGS_TYPES.HAND2]: initialInfParams,
  })

  // HOLD CHECKED BAG
  // ===
  bagParams = bagParams.set(
    BAGS_TYPES.HOLD,
    Map({
      length: bagParamsObj.get("hold_length"),
      width: bagParamsObj.get("hold_width"),
      height: bagParamsObj.get("hold_height"),
      weight: bagParamsObj.get("hold_weight"),
      note: bagParamsObj.get("hold_note"),
    }),
  )

  // PAID HAND BAGS
  // ===
  if (hasPaidHandBagsAllowed) {
    if (bagsRestrictions.get("hands")) {
      // MMB
      bagParams = bagParams.set(
        BAGS_TYPES.HAND,
        paramIntersection(bagParams.get(BAGS_TYPES.HAND), bagsRestrictions.get("hands")[2]),
      )
      bagParams = bagParams.set(
        BAGS_TYPES.HAND2,
        paramIntersection(bagParams.get(BAGS_TYPES.HAND2), bagsRestrictions.get("hands")[1]),
      )
      // bagParms = acc.set(BAGS_TYPES.HOLD, updateBagTypeParams(acc.get(BAGS_TYPES.HOLD), bagsRestrictions.get("holds")[1]))
    } else {
      // Reservation => flights list => intersection
      // Compute "intersection" (smallest possible values from restrictions)
      bagsRestrictions.toArray().forEach(flight => {
        // TODO: Super confusing HAND -> hands[2], HAND2 -> hands[1]
        // Keep until we can't use `bags_restrictions` for all -> rewrite logic of bags on reservation
        // New api changed indexation - retype to array for backward compatibility
        const handBagsArray = toArray(flight.hands)

        bagParams = bagParams.set(
          BAGS_TYPES.HAND,
          paramIntersection(bagParams.get(BAGS_TYPES.HAND), handBagsArray[1].restrictions),
        )
        bagParams = bagParams.set(
          BAGS_TYPES.HAND2,
          paramIntersection(bagParams.get(BAGS_TYPES.HAND2), handBagsArray[0].restrictions),
        )
        // bagParms = acc.set(BAGS_TYPES.HOLD, updateBagTypeParams(acc.get(BAGS_TYPES.HOLD), flight.holds[1].restrictions))
      })
    }
  } else {
    // HAND BAG
    // ===
    let handBagParams = {
      length: bagParamsObj.get("hand_length"),
      width: bagParamsObj.get("hand_width"),
      height: bagParamsObj.get("hand_height"),
      weight: bagParamsObj.get("hand_weight"),
      note: bagParamsObj.get("hand_note"),
    }

    // Add special infants hand bag weight
    if (infantsDifferentHandWeight) {
      handBagParams = {
        ...handBagParams,
        ...{ infantsWeight: infantsHandBagWeight },
      }
    }

    bagParams = bagParams.set(BAGS_TYPES.HAND, Map(handBagParams))

    // HAND 2 / PERSONAL ITEM BAG
    // ===
    bagParams = bagParams.set(
      BAGS_TYPES.HAND2,
      Map({
        length: bagParamsObj.get("hand2_length"),
        width: bagParamsObj.get("hand2_width"),
        height: bagParamsObj.get("hand2_height"),
        weight: bagParamsObj.get("hand2_weight"),
        note: bagParamsObj.get("hand2_note"),
      }),
    )
  }

  return bagParams
}
